/**
 * Hook for realtime renko updates via the unified SSE.
 *
 * The canonical renko series (8pt bricks) streams on the single channel
 * 'es:renko:8.0', which carries three event types:
 *   - renko_brick      : a completed brick { type, brick_size, data: <brick> }
 *   - renko_forming    : the in-progress brick (between completions) so the
 *                        chart ticks live like other candles { type, data: <brick> }
 *   - renko_rotations  : the rotations payload { type, data: { meta,
 *                        rotations: { legs, thresholds, ... } } }
 *
 * Mirrors useLiveVolumeCandles. The channel string is hard-coded because the
 * Python publisher uses f"es:renko:{8.0}" == 'es:renko:8.0', whereas JS
 * `es:renko:${8.0}` would yield 'es:renko:8'.
 */

import { useEffect, useRef, useState } from 'react';
import { unifiedSSE } from './useUnifiedSSE';

// Build the SSE channel for a brick size the way the Python publisher does:
// f"es:renko:{8.0}" == 'es:renko:8.0'. JS `${8.0}` yields '8', so format with one
// decimal to match (8 -> 'es:renko:8.0', 4 -> 'es:renko:4.0').
const renkoChannel = (size) => `es:renko:${Number(size).toFixed(1)}`;

export function useLiveRenkoBricks(onBrick, onForming, onRotations, enabled = true, brickSize = 8.0, onGap = null) {
  const onBrickRef = useRef(onBrick);
  const onFormingRef = useRef(onForming);
  const onRotationsRef = useRef(onRotations);
  const onGapRef = useRef(onGap);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    onBrickRef.current = onBrick;
    onFormingRef.current = onForming;
    onRotationsRef.current = onRotations;
    onGapRef.current = onGap;
  }, [onBrick, onForming, onRotations, onGap]);

  useEffect(() => {
    if (!enabled) { setConnected(false); return; }
    const RENKO_CHANNEL = renkoChannel(brickSize);

    const handleBrick = (message) => {
      if (onBrickRef.current && message && message.data) {
        onBrickRef.current(message.data);
        setConnected(true);
      }
    };
    const handleForming = (message) => {
      if (onFormingRef.current && message && message.data) {
        onFormingRef.current(message.data);
        setConnected(true);
      }
    };
    const handleRotations = (message) => {
      if (onRotationsRef.current && message && message.data) {
        onRotationsRef.current(message.data);
        setConnected(true);
      }
    };
    // Server-inserted overflow marker: a completed brick was evicted from this
    // client's SSE queue (stalled tab), so the local series has a permanent
    // hole until refetched. Payload: { channel, dropped }.
    const handleGap = (message) => {
      if (onGapRef.current && message) {
        onGapRef.current(message);
      }
    };

    const unsub1 = unifiedSSE.subscribe(RENKO_CHANNEL, 'renko_brick', handleBrick);
    const unsub2 = unifiedSSE.subscribe(RENKO_CHANNEL, 'renko_forming', handleForming);
    const unsub3 = unifiedSSE.subscribe(RENKO_CHANNEL, 'renko_rotations', handleRotations);
    const unsub4 = unifiedSSE.subscribe(RENKO_CHANNEL, 'gap', handleGap);
    console.log(`[LiveRenkoBricks] Subscribed to unified SSE channel: ${RENKO_CHANNEL}`);

    return () => {
      unsub1();
      unsub2();
      unsub3();
      unsub4();
      setConnected(false);
    };
  }, [enabled, brickSize]);   // resubscribe to the new channel when the size changes

  return { connected };
}

export default useLiveRenkoBricks;
